import {compact, isUndefined} from 'lodash';
import React, {FC, useCallback} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {getPreviewerEmojiDefinition} from '../../../../../models/api/previewerHelpers';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {openTopicLinkInBrowser} from '../../../../../services/browser';
import {Emoji} from '../../../../common/emoji/emoji';
import {CommentViewerBaseUnfurl} from './commentViewerBaseUnfurl';

/*
 * Props.
 */

interface CommentViewerTopicUnfurlProps {
  topic?: TopicModel;
  owner: TeammateModel | undefined;
  isInteractionDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledGridDivProps {
  $hasMiddleLine: boolean;
  $hasBottomLine: boolean;
}

const StyledGridDiv = styled.div<StyledGridDivProps>`
  position: relative;

  display: grid;
  grid-row-gap: 2px;
  grid-column-gap: 6px;
  ${(p) => computeTopicUnfurlGridTemplate(p)}

  padding: 10px 12px;
  background-color: ${(p) => p.theme.greys.shade10};
`;

const topArea = 'top-left top-line';
const middleArea = 'middle-left middle-line';
const bottomArea = 'bottom-left bottom-line';

function computeTopicUnfurlGridTemplate(props: StyledGridDivProps) {
  const templateAreas = compact([
    topArea,
    props.$hasMiddleLine && middleArea,
    props.$hasBottomLine && bottomArea,
  ]);

  return css`
    grid-template-columns: 16px 1fr;
    grid-template-rows: repeat(${templateAreas.length}, 20px);
    grid-template-areas: ${templateAreas.map((area) => `'${area}'`).join(' ')};
  `;
}

const StyledTopicIconDiv = styled.div`
  grid-area: top-left;
  display: grid;
`;

const StyledTopicNameDiv = styled.div`
  position: relative;

  grid-area: top-line;
  display: grid;

  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledTopicMiddleLinDiv = styled.div`
  position: relative;

  grid-area: middle-line;
  display: grid;
`;

const StyledTopicBottomLineDiv = styled.div`
  position: relative;

  grid-area: bottom-line;
  display: flex;
  flex-direction: row;
  align-items: center;

  padding-top: 2px;

  & > *:not(:first-child) {
    margin-left: 5px;
  }
`;

const StyledTopicStatusDiv = styled.div`
  width: fit-content;
  height: fit-content;

  padding: 0 5px;
  border-radius: 7px;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade60};

  border: 1px solid ${(p) => p.theme.greys.shade60};
`;

const StyledTopicOwnerDiv = styled.div`
  width: fit-content;

  display: flex;
  flex-direction: row;

  & > *:not(:first-child) {
    margin-left: 2px;
  }
`;

const StyledTextSpan = styled.span`
  ${ellipsis()};
`;

/*
 * Component.
 */

export const CommentViewerTopicUnfurl: FC<CommentViewerTopicUnfurlProps> = (props) => {
  const {topic, owner, isInteractionDisabled} = props;

  const onUnfurlClick = useCallback(() => topic && openTopicLinkInBrowser(topic.externalLink), [topic]);

  if (!topic) {
    return null;
  }

  return (
    <CommentViewerBaseUnfurl
      onUnfurlClick={onUnfurlClick}
      interactionId="commentViewerTopicUnfurl"
      isInteractionDisabled={isInteractionDisabled}
    >
      {renderUnfurlContents(topic, owner)}
    </CommentViewerBaseUnfurl>
  );
};

/*
 * Helpers.
 */

function renderUnfurlContents(topic: TopicModel, owner: TeammateModel | undefined) {
  const hasDescription = !isUndefined(topic.description);
  const hasStatus = !isUndefined(topic.status);
  const hasOwner = !isUndefined(owner);

  return (
    <StyledGridDiv $hasMiddleLine={hasDescription} $hasBottomLine={hasStatus || hasOwner}>
      <StyledTopicIconDiv>{renderUnfurlIcon(topic)}</StyledTopicIconDiv>
      <StyledTopicNameDiv>
        <StyledTextSpan>{topic.name}</StyledTextSpan>
      </StyledTopicNameDiv>
      {maybeRenderUnfurlMiddleLine(topic)}
      {maybeRenderUnfurlBottomLine(topic, owner)}
    </StyledGridDiv>
  );
}

function renderUnfurlIcon(topic: TopicModel) {
  if (topic.externalLinkType) {
    const emoji = getPreviewerEmojiDefinition(topic.externalLinkType);

    if (emoji) {
      return <Emoji size={16} {...emoji} />;
    }
  }

  return <Icon name="linkSmall" />;
}

function maybeRenderUnfurlMiddleLine(topic: TopicModel) {
  if (!topic.description) {
    return null;
  }

  return (
    <StyledTopicMiddleLinDiv>
      <StyledTextSpan>{topic.description}</StyledTextSpan>
    </StyledTopicMiddleLinDiv>
  );
}

function maybeRenderUnfurlBottomLine(topic: TopicModel, owner: TeammateModel | undefined) {
  if (!topic.status && !owner) {
    return null;
  }

  return (
    <StyledTopicBottomLineDiv>
      {topic.status && (
        <StyledTopicStatusDiv>
          <StyledTextSpan>{topic.status}</StyledTextSpan>
        </StyledTopicStatusDiv>
      )}
      {owner && (
        <StyledTopicOwnerDiv>
          <StyledTextSpan>{buildTeammateDisplayName(owner)}</StyledTextSpan>
        </StyledTopicOwnerDiv>
      )}
    </StyledTopicBottomLineDiv>
  );
}
